import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { Region } from '@cds/betting-offer/tags';
import { RacingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { WinGameFixturePreview } from '@frontend/sports/racing/feature/models';
import { IntlService } from '@frontend/vanilla/core';
import { mapValues, orderBy, uniqBy } from 'lodash-es';

import { CrmPromotionProviderService, PromotionMap } from '../../../crm-promotion/crm-promotion-provider.service';
import { convertDayOfRaceToUrlParam } from '../../../horse-racing-shared/helpers';
import { DayOfRaceModel } from '../../../horse-racing-shared/model/day-of-race.model';

interface MeetingViewModel {
    fixtures: WinGameFixturePreview[];
    link: string;
    meetingName: string;
    meetingId: number;
}

export interface RegionViewModel {
    name: string;
    id: number[];
    meetings: MeetingViewModel[];
}

export interface DayOfRaceViewModel {
    regions: RegionViewModel[];
}
@Component({
    template: '',
    standalone: false,
})
export abstract class DayofRaceComponent {
    protected _dayOfRace: DayOfRaceModel;
    protected _filter: DayOfRaceDate;
    protected _sportId: SportConstant;
    vm: DayOfRaceViewModel;
    protected readonly destroyRef = inject(DestroyRef);
    constructor(protected urlHelper: UrlHelperService) {}

    @Input() set dayOfRace(value: DayOfRaceModel) {
        this._dayOfRace = value;
        if (this.isInitialized) {
            this.setVM();
        }
    }

    @Input() set filter(value: DayOfRaceDate) {
        this._filter = value;
        if (this.isInitialized) {
            this.setVM();
        }
    }

    @Input() set sportId(value: SportConstant) {
        this._sportId = value;
        if (this.isInitialized) {
            this.setVM();
        }
    }

    protected setVM(): void {
        this.vm = DayofRaceComponent.buildVM(this._dayOfRace, this._filter, this.urlHelper, this._sportId);
    }

    get isInitialized(): boolean {
        return typeof this._filter !== 'undefined' && typeof this._dayOfRace !== 'undefined' && typeof this._sportId !== 'undefined';
    }

    private static buildVM(value: DayOfRaceModel, filter: DayOfRaceDate, urlHelper: UrlHelperService, sportId: SportConstant): DayOfRaceViewModel {
        const fixtures = value.fixtures;
        const reg = mapValues(value.regions, (regionList, position) => {
            let regionsWithFixtures: Region[] = fixtures.filter((f) => regionList.some((r) => r.regionId === f.region.id)).map((f) => f.region);
            regionsWithFixtures = uniqBy(regionsWithFixtures, (r) => r.id);

            const regionsWithMeetings = regionList.flatMap((region) => region.meetings.map((meeting) => ({ region, meeting })));

            return {
                name: regionsWithFixtures.map((r) => translationToStr(r.name)).join(' & '),
                id: regionsWithFixtures.map((r) => r.id),
                meetings: regionsWithMeetings.map((x) => {
                    const fs = fixtures.filter((f) => f.region.id === x.region.regionId && f.meeting.id === x.meeting);
                    const meeting = fs[0].meeting;

                    return {
                        fixtures: fs,
                        meetingName: translationToStr(meeting.name),
                        meetingId: meeting.id,
                        link: urlHelper.getRaceUrl(
                            sportId,
                            { id: meeting.id, name: translationToStr(meeting.name) },
                            convertDayOfRaceToUrlParam(filter),
                        ),
                    };
                }),
                position: +position,
            };
        });

        return {
            regions: orderBy(reg, (r) => r.position).map((r) => {
                //@ts-ignore it was meant as a temp property.
                delete r.position;

                return r;
            }),
        };
    }
}

@Component({
    selector: 'ms-day-of-race-lobby',
    templateUrl: 'day-of-race-lobby.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DayOfRaceLobbyComponent extends DayofRaceComponent implements OnInit {
    protected override _dayOfRace: DayOfRaceModel;
    protected override _filter: DayOfRaceDate;
    protected override _sportId: SportConstant;

    promotedFixtureIdsMap: StringDictionary<string> = {};
    promotedMeetingIdsMap: StringDictionary<number> = {};
    showLegend: boolean;
    isGreyhoundsStyle = false;

    constructor(
        public sitecore: Sitecore,
        protected override urlHelper: UrlHelperService,
        private crmPromotionProvider: CrmPromotionProviderService,
        protected changeDetection: ChangeDetectorRef,
        private intlService: IntlService,
        private racingConfig: RacingConfig,
    ) {
        super(urlHelper);
    }

    ngOnInit(): void {
        this.showLegend = this._filter === DayOfRaceDate.Today;
        if (this._sportId === SportConstant.Greyhounds) {
            this.isGreyhoundsStyle = true;
        }
        if (this._sportId === SportConstant.Horses) {
            this._dayOfRace.fixtures.forEach((fixture) => {
                if (this._filter === DayOfRaceDate.Tomorrow) {
                    fixture.addons.bestOddsGuarantee = false;
                } else {
                    const startTime = this.intlService.formatDate(fixture.startDate, 'HH:mm');
                    if (startTime <= this.racingConfig.bogTodayThresholdTime) {
                        fixture.addons.bestOddsGuarantee = false;
                    }
                }
            });
        }
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids) => this.promotionMapChanged(ids));
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.promotedFixtureIdsMap = promoMap.fixtures;
        this.promotedMeetingIdsMap = promoMap.meetings;
        this.changeDetection.markForCheck();
    }
}
